import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgePercent, Calculator, ChevronRight, FileSearch, ListChecks, MapPin, Quote, Star } from "lucide-react";
import { api } from "../../lib/api";
import { quoteHref } from "../../data/homeContent";
import { cityPath } from "../../lib/werkgebied";
import { useKbCategories } from "../../lib/kennisbank";
import { InfoSection } from "../info/InfoSections";
import { RouteCards } from "../info/InfoExtras";
import { SectionHeading } from "../home/CtaButtons";
import { ArticleCard } from "../kennisbank/ArticleCard";
import { ProjectCard } from "../portfolio/ProjectCard";

const useList = (url, params) => {
  const [list, setList] = useState([]);
  const key = JSON.stringify(params);
  useEffect(() => {
    let on = true;
    api.get(url, { params }).then((r) => on && setList(r.data || [])).catch(() => on && setList([]));
    return () => { on = false; };
  }, [url, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return list;
};

// Drie startroutes; de plaats gaat automatisch mee naar de offertewizard.
export const CityRoutes = ({ page }) => {
  const items = [
    { icon: Calculator, href: quoteHref(page.onderwerp, { woonplaats: page.name }), primary: true, track: "cta_quote_click", cta: "Start de prijsindicatie",
      title: "Vrijblijvende prijsindicatie", text: `Beantwoord een paar korte vragen over uw woning in ${page.name}. Foto's meesturen kan; u zit nergens aan vast.` },
    { icon: FileSearch, href: "/offerte-vergelijken", track: "cta_compare_click", cta: "Offerte laten vergelijken",
      title: "Bestaande offerte vergelijken", text: "Heeft u al een offerte? Wij leggen hem naast uw wensen en laten zien waar de verschillen zitten." },
    page.show_isde
      ? { icon: BadgePercent, href: "/isde-subsidie-kozijnen#subsidiecheck", track: "cta_isde_click", cta: "Doe de ISDE-check",
        title: "ISDE-subsidiecheck", text: "Controleer vooraf of nieuwe kozijnen met isolatieglas in aanmerking kunnen komen voor subsidie." }
      : { icon: ListChecks, href: "/werkwijze", track: "cta_werkwijze_click", cta: "Bekijk de werkwijze",
        title: "Onze werkwijze", text: "Van aanvraag en inmeten tot montage en oplevering: zo verloopt het." },
  ];
  return (
    <InfoSection id="starten" tone="slate" eyebrow="Starten" title="Drie manieren om te beginnen" lead="Kies de route die bij uw situatie past. Alle drie zijn vrijblijvend.">
      <RouteCards items={items} location={`city-${page.slug}`} />
    </InfoSection>
  );
};

// Alleen echt uitgevoerde en gepubliceerde projecten met deze plaats; zonder projecten verschijnt de sectie niet.
export const CityProjects = ({ page }) => {
  const projects = useList("/projects", { city: page.slug, limit: 6 });
  if (!projects.length) return null;
  return (
    <InfoSection id="projecten" eyebrow="Uitgevoerd werk" title={`Projecten in ${page.name}`} testid="city-projects">
      <ul className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">{projects.map((p) => <li key={p.slug}><ProjectCard p={p} /></li>)}</ul>
    </InfoSection>
  );
};

export const CityReviews = ({ page }) => {
  const reviews = useList("/reviews", { city: page.slug, limit: 6 });
  if (!reviews.length) return null;
  return (
    <InfoSection id="ervaringen" eyebrow="Klantervaringen" title={`Ervaringen uit ${page.name} en omgeving`} testid="city-reviews-section">
      <ul className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {reviews.map((r) => (
          <li key={r.id} className="card flex flex-col p-6" data-testid={`city-review-${r.id}`}>
            <div className="flex items-center justify-between">
              <Quote className="h-5 w-5 text-gold" aria-hidden="true" />
              <span className="flex gap-0.5 text-gold" aria-label={`${r.rating || 5} van 5 sterren`}>
                {[...Array(Math.max(1, Math.min(5, r.rating || 5)))].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" aria-hidden="true" />)}
              </span>
            </div>
            <blockquote className="mt-4 flex-1 text-[15px] leading-relaxed text-slate-700">“{r.text}”</blockquote>
            <p className="mt-5 border-t border-slate-100 pt-4 text-[14px] font-semibold text-ink">{r.name}{r.place && <span className="block text-[12.5px] font-normal text-slate-500">{r.place}</span>}</p>
          </li>
        ))}
      </ul>
      <p className="mt-5 text-[12.5px] text-slate-500">Voorbeelden van klantervaringen. Vraag bij uw aanvraag gerust naar referenties in uw buurt.</p>
    </InfoSection>
  );
};

export const CityArticles = ({ page }) => {
  const cats = useKbCategories();
  if (!page.articles?.length) return null;
  return (
    <section aria-labelledby="city-kb-title" className="section" data-testid="city-articles">
      <div className="container-tight">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionHeading id="city-kb-title" eyebrow="Kennisbank" title="Lees meer voordat u kiest" />
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-maas hover:underline" data-testid="city-articles-all">Alle artikelen <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
        </div>
        <ul className="mt-8 grid gap-5 md:grid-cols-3">
          {page.articles.map((a) => <li key={a.slug}><ArticleCard a={a} cats={cats} testid={`city-article-${a.slug}`} /></li>)}
        </ul>
      </div>
    </section>
  );
};

export const CityOthers = ({ page }) => {
  if (!page.others?.length) return null;
  return (
    <section aria-labelledby="city-others-title" className="section-tight border-t border-slate-200 bg-slate-50" data-testid="city-others">
      <div className="container-tight">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionHeading id="city-others-title" eyebrow="Werkgebied" title="Andere plaatsen in ons werkgebied" />
          <Link to="/werkgebied" className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-maas hover:underline" data-testid="city-others-all">Heel ons werkgebied <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
        </div>
        <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {page.others.map((c) => (
            <li key={c.slug}>
              <Link to={cityPath(c.slug)} className="group flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 transition-colors hover:border-maas" data-testid={`city-other-${c.slug}`}>
                <span className="flex items-center gap-2 text-[14.5px] font-medium text-ink"><MapPin className="h-4 w-4 text-gold" aria-hidden="true" />Kozijnen {c.name}</span>
                <ChevronRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};
